// ABOUTME: Quantity field of a catalog table row, bound to the cart: it shows the product's quantity in it.
// ABOUTME: Raising it adds the product or raises its first line, lowering it lowers its lines (removed at zero).

import { AddIcon, Box, IconButton, NumberField, Tooltip } from '@ds/coolshop';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import { useCartContext } from '../Cart/CartContext';
import { useCartItemActions } from '../Cart/useCartItemActions';

type CartLineItem = { id?: unknown; contents?: unknown };
type LineItemContent = {
    contentTypeGroup: string;
    contentType: string;
    value?: Array<{ value?: string[] }>;
};

const readLineInfo = (item: CartLineItem, field: string): string =>
    ((item.contents ?? []) as LineItemContent[]).find(
        content => content.contentTypeGroup === 'line_item_info' && content.contentType === field,
    )?.value?.[0]?.value?.[0] ?? '';

/** The cart lines holding the product, as a model or as a prototype. */
const linesOf = (lineItems: CartLineItem[], productId: string) =>
    lineItems.filter(
        item =>
            readLineInfo(item, 'model') === productId ||
            readLineInfo(item, 'prototype') === productId,
    );

const quantityOf = (item: CartLineItem) => parseInt(readLineInfo(item, 'quantity'), 10) || 1;

interface RangeTableCartStepperProps {
    /** The node id of the product, as the cart lines record it. */
    productId: string;
    /** Adds a product not in the cart yet in the given quantity, the way the catalog adds a product. */
    onAdd: (quantity: number) => Promise<void>;
    /**
     * Shows and changes the product's quantity in the cart (default). Off where the row adds to
     * something else, such as the offer of the item selection dialog: the row then only adds.
     */
    bindToCart?: boolean;
}

export function RangeTableCartStepper({
    productId,
    onAdd,
    bindToCart = true,
}: RangeTableCartStepperProps) {
    const { formatMessage } = useIntl();
    const cart = useCartContext();
    // A quantity change and a removal show their progress and outcome, as the add does.
    const { updateQuantity, removeItem } = useCartItemActions();
    const [busy, setBusy] = useState(false);
    // The quantity asked for while its change is in flight: the field shows it until the change
    // ends, then the cart's own quantity again, the same one after a change that went through, the
    // previous one after a change that failed.
    const [pending, setPending] = useState<number>();

    const run = async (change: () => Promise<void>) => {
        setBusy(true);
        try {
            await change();
        } finally {
            setBusy(false);
        }
    };

    // Without a cart there is no quantity to show: the row adds one unit at a time, as a card does.
    if (!bindToCart || !cart?.enabled) {
        const addLabel = formatMessage({ id: 'cpq.catalog.table.add', defaultMessage: 'Add' });
        return (
            <Tooltip title={addLabel}>
                <span>
                    <IconButton
                        size="small"
                        color="primary"
                        aria-label={addLabel}
                        disabled={busy}
                        onClick={() => run(() => onAdd(1))}
                    >
                        <AddIcon fontSize="small" />
                    </IconButton>
                </span>
            </Tooltip>
        );
    }

    // The quantity is read from the cart every render, so it never drifts from it.
    const lines = linesOf((cart.cart?.lineItems ?? []) as CartLineItem[], productId);
    const quantity = lines.reduce((total, line) => total + quantityOf(line), 0);
    const [firstLine] = lines;

    // The field steps by one or takes a typed quantity, already clamped to zero and above, and shows
    // the product's total over its lines: a raise goes to the first line, a cut is taken from the
    // lines in turn, from the last, so the total reaches the new quantity whatever the lines hold.
    const handleChange = (next: number) => {
        setPending(next);
        return run(async () => {
            if (!firstLine) {
                if (next > 0) await onAdd(next);
                return;
            }
            if (next >= quantity) {
                await updateQuantity(
                    firstLine.id as string,
                    quantityOf(firstLine) + (next - quantity),
                );
                return;
            }
            const { steps } = [...lines].reverse().reduce<{
                excess: number;
                steps: { lineItemId: string; quantity: number }[];
            }>(
                (plan, line) => {
                    if (plan.excess <= 0) return plan;
                    const lineQuantity = quantityOf(line);
                    const kept = Math.max(0, lineQuantity - plan.excess);
                    return {
                        excess: plan.excess - (lineQuantity - kept),
                        steps: [...plan.steps, { lineItemId: line.id as string, quantity: kept }],
                    };
                },
                { excess: quantity - next, steps: [] },
            );
            // One cart change at a time, each line lowered or removed in turn; a failed change stops
            // the cut there (its toast reports it), so no further line is touched.
            await steps.reduce<Promise<boolean>>(
                (previous, step) =>
                    previous.then(done => {
                        if (!done) return false;
                        return step.quantity > 0
                            ? updateQuantity(step.lineItemId, step.quantity)
                            : removeItem(step.lineItemId);
                    }),
                Promise.resolve(true),
            );
        }).finally(() => setPending(undefined));
    };

    // The row actions sit in a box that clips what overflows it, cut to the height of its content:
    // the room above and below keeps the field's elevation shadow from being cut off at the bottom.
    return (
        <Box py={1}>
            <NumberField
                size="small"
                value={pending ?? quantity}
                onChange={handleChange}
                min={0}
                disabled={busy}
                decrementAriaLabel={formatMessage({
                    id: 'cpq.cart.decreaseQuantity',
                    defaultMessage: 'Decrease quantity',
                })}
                incrementAriaLabel={formatMessage({
                    id: 'cpq.cart.increaseQuantity',
                    defaultMessage: 'Increase quantity',
                })}
            />
        </Box>
    );
}
